import { memo, useRef, useState } from 'react';

import { Icon } from '~/components/Icon';
import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';

interface SearchBarProps {
  initialSearchText?: string;
  placeholder: string;
  onSubmit: (searchValue: string) => void;
  onClear: () => void;
  onChange?: (value: string) => void;
  formAriaLabel: string;
  inputAriaLabel: string;
}

/**
 * This component is wrapped in React.memo() to prevent unnecessary re-renders with
 * the ScrollCollapse logic. See https://github.com/wmgtech/wmg2-one-app/pull/1736
 */
export const SearchBar: React.FC<SearchBarProps> = memo(
  ({
    initialSearchText,
    placeholder,
    onSubmit,
    onClear,
    onChange,
    formAriaLabel,
    inputAriaLabel,
  }: SearchBarProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState<string>(
      initialSearchText ?? '',
    );

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;

      setInputValue(newValue);
      onChange?.(newValue);
    };

    const handleClearButtonClick = () => {
      inputRef.current?.focus();
      setInputValue('');
      onClear();
    };

    return (
      <form
        className="group relative flex grow items-center"
        role="search"
        aria-label={formAriaLabel}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(inputValue);
        }}
      >
        <div className="pointer-events-none absolute pl-md duration-300 ease-out text-subtle group-focus-within:text-default">
          <Icon type="search" variant="light" size="sm" aria-hidden />
        </div>
        <input
          ref={inputRef}
          type="text"
          name="search"
          className={cn(
            'grow place-self-stretch rounded-xs py-sm pl-2xl text-body-sm leading-8 duration-300 ease-out',
            'outline outline-1 bg-emphasis-default outline-emphasis-subtle',
            'hover:bg-emphasis-strong hover:outline-emphasis-stronger',
            'placeholder:duration-300 focus:placeholder:text-default',
          )}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          aria-label={inputAriaLabel}
        />
        {inputValue ? (
          <IconButton
            type="button"
            iconType="circle-xmark"
            size="small"
            className="absolute right-sm duration-300 ease-out text-subtle focus-within:text-icon-strong hover:text-icon-strong"
            onClick={handleClearButtonClick}
            aria-label="Clear search input"
          />
        ) : null}
      </form>
    );
  },
);

SearchBar.displayName = 'SearchBar';
